import { useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { ImpersonationBanner } from '@/components/ImpersonationBanner';
import { PausedSessionOverlay } from '@/components/PausedSessionOverlay';
import { TrialBanner } from '@/components/TrialBanner';
import { IntercomProvider } from '@/components/IntercomProvider';
import { OwnerTwoFactorDialog } from '@/components/OwnerTwoFactorDialog';
import { InactivityWarningDialog } from '@/components/InactivityWarningDialog';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import { useRegisterSession } from '@/hooks/useRegisterSession';
import { usePermissions } from '@/hooks/usePermissions';
import { useRealtimeReconnect } from '@/hooks/useRealtimeReconnect';
import { useSentryUser } from '@/hooks/useSentryUser';
import { cn } from '@/lib/utils';

export function RootLayout() {
  const { isImpersonating } = useImpersonation();
  const { effectiveRole } = usePermissions();
  const { isSessionPaused } = useRegisterSession();
  
  // Handle Safari's aggressive WebSocket throttling
  // Reconnects realtime channels when page becomes visible
  useRealtimeReconnect();
  useSentryUser();
  
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Only show paused overlay for cashiers/employees
  const isCashierRole = effectiveRole === 'cashier' || effectiveRole === 'employee';
  const showPausedOverlay = isCashierRole && isSessionPaused;

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  return (
    <IntercomProvider>
      <div className="min-h-screen bg-background text-foreground">
        {/* Global Owner 2FA Approval Dialog */}
        <OwnerTwoFactorDialog />

        {/* Global Inactivity Warning Dialog */}
        <InactivityWarningDialog />

        {/* Paused Session Overlay - blocks UI when cashier is on break */}
        {showPausedOverlay && <PausedSessionOverlay />}

        {/* Impersonation Banner */}
        <ImpersonationBanner />

        <div
          className={cn(
            'flex h-screen',
            isImpersonating && 'pt-12' // Add padding when impersonation banner is shown
          )}
        >
          <Sidebar
            collapsed={sidebarCollapsed}
            onToggle={handleSidebarToggle}
          />
          <div className="flex flex-1 flex-col min-w-0 overflow-hidden">
            <TrialBanner />
            <div className="flex-1 min-h-0 overflow-hidden">
              <Outlet />
            </div>
          </div>
        </div>
      </div>
    </IntercomProvider>
  );
}


